/**
 * Video Editor — project workspace.
 *
 * The controller for the operator flow: fetches the project, derives which
 * surface to show from the lifecycle status, dispatches staged pipeline jobs
 * and polls them to completion, and routes between surfaces.
 *
 * The flow is three operator steps — Brief → Storyboard → Done — with Building
 * and Rendering as transient progress screens. After the brief, analyse and
 * plan run back-to-back automatically, so the operator never triggers a
 * pipeline stage by hand: they land straight on the storyboard.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import type { User, ProjectDetail, VideoProjectStatus, VideoJob } from './types';
import { useVideoEditorApi, PipelineCommand } from './videoEditorApi';
import BriefSurface from './surfaces/BriefSurface';
import StoryboardSurface from './surfaces/StoryboardSurface';
import DoneSurface from './surfaces/DoneSurface';
import AICoPilot from './surfaces/AICoPilot';
import { JobProgress } from './surfaces/parts';

export type Surface = 'brief' | 'building' | 'storyboard' | 'rendering' | 'done';

// The co-pilot panel uses the spec's surface id for per-surface framing.
const SPEC_SURFACE: Record<string, string> = {
  building: 'S3', storyboard: 'S5', rendering: 'S6', done: 'S7'
};

function defaultSurface(status: VideoProjectStatus): Surface {
  switch (status) {
    case 'created':
    case 'raw-imported': return 'brief';
    case 'analysing':    return 'building';
    case 'planning':     return 'storyboard';
    case 'rendering':    return 'rendering';
    case 'in-review':
    case 'stored':
    case 'closed':       return 'done';
    default:             return 'brief';
  }
}

// The three operator-facing steps. Building/Rendering are transient progress
// screens, not steps — they map onto the step they belong to (see STEP_OF).
type StepKey = 'brief' | 'storyboard' | 'done';
const STEPS: { key: StepKey; label: string }[] = [
  { key: 'brief',      label: 'Brief' },
  { key: 'storyboard', label: 'Storyboard' },
  { key: 'done',       label: 'Done' },
];
const STEP_OF: Record<Surface, StepKey> = {
  brief: 'brief', building: 'brief', storyboard: 'storyboard',
  rendering: 'storyboard', done: 'done',
};

// Which steps are reachable, derived from durable project state — a step
// unlocks once the work it depends on exists.
function reachedSteps(p: ProjectDetail['project']): Set<StepKey> {
  const s = new Set<StepKey>(['brief']);
  if (p.editPlan || (p.variants && p.variants.length > 0)) s.add('storyboard');
  if (p.renderOutput && p.renderOutput.vmPath) s.add('done');
  return s;
}

interface Props {
  projectId: string;
  user: User | null;
  onBack: () => void;
}

const ProjectWorkspace: React.FC<Props> = ({ projectId, user, onBack }) => {
  const api = useVideoEditorApi(user);

  const [detail, setDetail] = useState<ProjectDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [surface, setSurface] = useState<Surface | null>(null);
  const [activeJob, setActiveJob] = useState<VideoJob | null>(null);
  const pollRef = useRef<any>(null);

  const load = useCallback(async (keepSurface?: boolean) => {
    try {
      setError(null);
      const d = await api.getProject(projectId);
      setDetail(d);
      if (!keepSurface) {
        setSurface(prev => prev || defaultSurface(d.project.status));
      }
      return d;
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load project');
      return null;
    } finally {
      setLoading(false);
    }
  }, [api, projectId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [projectId]);
  useEffect(() => () => { if (pollRef.current) clearTimeout(pollRef.current); }, []);

  // Dispatch a staged pipeline command, then poll the job to completion.
  // `input` is forwarded (e.g. { segment } for a per-segment render).
  const runCommand = useCallback(async (
    command: PipelineCommand, onDone: (j: VideoJob) => void, input?: any
  ) => {
    try {
      setError(null);
      const job = await api.dispatch(projectId, command, input);
      setActiveJob(job);
      const poll = async () => {
        try {
          const j = await api.getJob(job._id);
          setActiveJob(j);
          if (j.status === 'succeeded' || j.status === 'failed') {
            const d = await load(true);
            if (j.status === 'failed') {
              setError(`Pipeline ${command} failed: ${j.error || 'unknown error'}`);
            }
            setActiveJob(null);
            if (d && j.status === 'succeeded') onDone(j);
            return;
          }
          pollRef.current = setTimeout(poll, 1500);
        } catch (e: any) {
          setError(e.message || 'Lost track of the pipeline job');
          setActiveJob(null);
        }
      };
      pollRef.current = setTimeout(poll, 1200);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || `Failed to start ${command}`);
    }
  }, [api, projectId, load]);

  if (loading) {
    return <div className="video-editor"><div className="ve-state">Loading project…</div></div>;
  }
  if (!detail) {
    return (
      <div className="video-editor">
        <button className="ve-detail__back" onClick={onBack}>← Back to projects</button>
        <div className="ve-state ve-state--error">{error || 'Project not found'}</div>
      </div>
    );
  }

  const { project } = detail;
  const current = surface || defaultSurface(project.status);
  const reachable = reachedSteps(project);
  const havePlan = !!(project.editPlan || (project.variants && project.variants.length > 0));

  // Kick off the brief → storyboard machine work: analyse, then plan, with no
  // manual stage triggers. Skips whichever stage is already done.
  const buildToStoryboard = () => {
    if (havePlan) { setSurface('storyboard'); return; }
    setSurface('building');
    if (project.analysis) {
      runCommand('plan', () => setSurface('storyboard'));
    } else {
      runCommand('analyse', () => runCommand('plan', () => setSurface('storyboard')));
    }
  };

  return (
    <div className="video-editor">
      <button className="ve-detail__back" onClick={onBack}>← Back to projects</button>

      {/* Step rail — Brief · Storyboard · Done. Jump to any step already
          reached; navigation is disabled while a pipeline job runs. */}
      <nav className="ve-stepper" aria-label="Project steps">
        {STEPS.map((step, i) => {
          const isReached = reachable.has(step.key);
          const isCurrent = STEP_OF[current] === step.key;
          return (
            <button
              key={step.key}
              type="button"
              className={`ve-step${isCurrent ? ' ve-step--current' : ''}` +
                `${isReached ? '' : ' ve-step--locked'}`}
              disabled={!isReached || isCurrent || !!activeJob}
              onClick={() => setSurface(step.key)}
              title={isReached
                ? (isCurrent ? `You are on ${step.label}` : `Go to ${step.label}`)
                : `${step.label} — not available yet`}
            >
              <span className="ve-step__n">{i + 1}</span>
              <span className="ve-step__l">{step.label}</span>
            </button>
          );
        })}
      </nav>

      {error && <div className="ve-banner ve-banner--error">{error}</div>}

      {current === 'brief' && (
        <BriefSurface
          project={project}
          api={api}
          onRefresh={() => load(true)}
          onStartAnalysis={buildToStoryboard}
        />
      )}

      {current === 'building' && (
        <JobProgress
          label="Build the cut"
          phase="building"
          job={activeJob}
          project={project}
          onStart={buildToStoryboard}
          onContinue={havePlan ? () => setSurface('storyboard') : undefined}
        />
      )}

      {current === 'storyboard' && (
        <StoryboardSurface
          project={project}
          api={api}
          onRefresh={() => load(true)}
          onRunPlan={() => {
            setSurface('building');
            runCommand('plan', () => setSurface('storyboard'));
          }}
          onRender={() => {
            setSurface('rendering');
            runCommand('render', () => setSurface('done'));
          }}
          onRenderSegment={(i: number) => {
            setSurface('rendering');
            runCommand('render', () => setSurface('done'), { segment: i });
          }}
          activeJob={activeJob}
        />
      )}

      {current === 'rendering' && (
        <JobProgress
          label="Render the cut"
          phase="rendering"
          job={activeJob}
          project={project}
          onStart={() => runCommand('render', () => setSurface('done'))}
          onContinue={project.renderOutput?.vmPath ? () => setSurface('done') : undefined}
        />
      )}

      {current === 'done' && (
        <DoneSurface
          project={project}
          api={api}
          onRefresh={() => load(true)}
          onReRender={() => setSurface('storyboard')}
        />
      )}

      {/* The AI co-pilot panel — surfaces S3-S7. The Brief step has its own
          conversational brief. */}
      {SPEC_SURFACE[current] && (
        <AICoPilot
          project={project}
          surface={SPEC_SURFACE[current]}
          api={api}
          onRefresh={() => load(true)}
        />
      )}
    </div>
  );
};

export default ProjectWorkspace;
